import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,SelectChangeEvent,Skeleton,TextField } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useMutation,useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from "../../../helpers/matomo";
import Tickets from "../../../service/Tickets";
import Users from "../../../service/Users";
import { userBackObj } from "../../Users/ListaUsuarios";

type props = {
  openRedirect: boolean,
  setOpenRedirect: Function,
  chamadoId: string,
  onPendingChange?: (pending: boolean) => void,
  onSuccess?: () => void,
};

const usersService = new Users();
const ticketsService = new Tickets();

export default function RedirectModal({
  openRedirect,
  setOpenRedirect,
  chamadoId,
  onPendingChange,
  onSuccess,

}: props) {
  const { t } = useTranslation('translation');
  const [encaminhadoPara, setEncaminhadoPara] = useState<string>('');
  const [motivoEncaminhamento, setMotivoEncaminhamento] = useState<string>('');

  const { data, isLoading, } = useQuery({
    queryKey: ['GET_USERS'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const body = { ...token };
        const result = await usersService.getAllUsers(body);
        if (!result || !result.users) throw new Error('No users found');
        return result.users;
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });


  const { mutate, isPending } = useMutation({
    mutationKey: ['REDIRECT_TICKET'],
    mutationFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const id = chamadoId.includes('_')
          ? chamadoId
          : `${chamadoId}_${token.userEmpresa}`;
        const promise = toast.promise(async () => {
          const result = await ticketsService.redirecionaChamado(token, id, motivoEncaminhamento, encaminhadoPara, 'redireciona');
          if (!result) throw new Error('Error redirecting ticket');
          return result;
        }, {
          pending: `${t('notifications.modals.redirectingTicket')}`,
          success: `${t('notifications.modals.ticketRedirected')}`,
          error: `${t('notifications.modals.errorRedirecting')}`,
        }, {
          toastId: 'redirect',
        });
        return promise;
      } catch (error) {
        console.error("Error redirecting ticket:", error);
      }
    },
    onSuccess: () => {
      onPendingChange?.(false);
      onSuccess?.();
      setOpenRedirect();
      trackEventMatomo('modal redireciona chamado', 'click', 'buttob', 'redireciona');
    }
  });

  useEffect(() => {
    onPendingChange?.(isPending);
  }, [isPending, onPendingChange]);

  return (
    <Dialog
      open={openRedirect}
      onClose={() => {
        if (!isPending) setOpenRedirect();
      }}
      scroll={'paper'}
    >
      <DialogTitle>{t('notifications.modals.redirectTicket')} </DialogTitle>
      <IconButton
        disabled={isPending}
        aria-label="close"
        onClick={() => setOpenRedirect()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        {isLoading ? <Skeleton variant="rounded" height={40} width='100%' /> :
          <>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">{t('notifications.modals.selectUser')}</InputLabel>
              <Select
                disabled={isPending}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={t('notifications.modals.selectUser')}
                onClick={() => trackEventMatomo('modal redireciona chamado', 'click', 'select', 'selciona valor')}
                onChange={(e: SelectChangeEvent<string>) => {
                  setEncaminhadoPara(e.target.value);
                }}
              >
                <MenuItem value='' >{t('select')}</MenuItem>
                {data.sort((a: userBackObj, b: userBackObj) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1; })
                  ?.filter((e: userBackObj) => e.status === 'ativo')
                  ?.map((e: userBackObj, i: number) => (
                    <MenuItem value={e.email} key={i}>{e.nome}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ marginTop: '20px' }}
              fullWidth
              multiline
              rows={3}
              label={t('notifications.modals.writeReasonForRedirecting')}
              onClick={() => trackEventMatomo('modal redireciona chamado', 'click', 'textarea', 'edita valor')}
              className="textarea"
              value={motivoEncaminhamento}
              disabled={isPending}
              onChange={(e) => setMotivoEncaminhamento(e.target.value)}
              style={{ resize: 'none' }}
            />
          </>}
      </DialogContent>
      <DialogActions>
        <Button disabled={isPending} onClick={() => mutate()}>
          {t('notifications.modals.redirect')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
